/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import { ApiStatus } from '../../../../ts';
import type { Instance } from '../../../../ts/instance';
import localStorage, { LocalStorage } from '../../util/localStorage';
import { listNewInstances, listUpdatedInstances } from './actions';
import { adapter, sortComparer } from './adapter';

// Initial State
export const initialState = Object.freeze(
  adapter.getInitialState({
    // The status of the asynchronous operations could be idle, pending, fulfilled, or error.
    listNewStatus: ApiStatus.IDLE,
    listNewError: null,
    listUpdatedStatus: ApiStatus.IDLE,
    listUpdatedError: null,
    instance: null,
    inaccessibleAccountNames: []
  })
);

const saveInstances = (instances: Instance[]): void => {
  localStorage.set(LocalStorage.INSTANCES, instances.sort(sortComparer));
};

// Slice + Reducer
const slice = createSlice({
  name: 'instances',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(listNewInstances.pending, state => {
        state.listNewStatus = ApiStatus.LOADING;
        state.listNewError = null;
      })
      .addCase(listNewInstances.fulfilled, (state, action) => {
        state.listNewStatus = ApiStatus.SUCCEEDED;
        state.listNewError = null;

        const { resources, inaccessibleAccountNames = [] } = action.payload;

        state.inaccessibleAccountNames = inaccessibleAccountNames;

        // Cache the retrieved instances in local storage.
        saveInstances(resources);

        adapter.setAll(state, resources);
      })
      .addCase(listNewInstances.rejected, (state, action) => {
        // Reset the state to its initial values.
        _.assign(state, initialState);
        state.listNewStatus = ApiStatus.ERROR;
        state.listNewError = action.error;

        // If there is an error listing instances remove the cached instances
        localStorage.remove(LocalStorage.INSTANCES);
      })

      // Fetch updated instances.
      .addCase(listUpdatedInstances.pending, state => {
        state.listUpdatedStatus = ApiStatus.LOADING;
        state.listUpdatedError = null;
      })
      .addCase(listUpdatedInstances.fulfilled, (state, action) => {
        state.listUpdatedStatus = ApiStatus.SUCCEEDED;
        state.listUpdatedError = null;

        const { resources, inaccessibleAccountNames = [] } = action.payload;

        state.inaccessibleAccountNames = inaccessibleAccountNames;

        // Cache the retrieved instances in local storage.
        saveInstances(resources);

        adapter.setAll(state, resources);
      })
      .addCase(listUpdatedInstances.rejected, (state, action) => {
        // Reset the state to its initial values.
        _.assign(state, initialState);
        state.listUpdatedStatus = ApiStatus.ERROR;
        state.listUpdatedError = action.error;

        // If there is an error listing instances remove the cached instances
        localStorage.remove(LocalStorage.INSTANCES);
      });
  }
});

export default slice.reducer;
